var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Literal } from 'lincd/models';
import { getPackageShape, linkedShape } from '../package.js';
import { Action } from './Action.js';
import { irlcg } from '../ontologies/irlcg.js';
import { literalProperty, objectProperty } from 'lincd/shapes/SHACL';
import { xsd } from 'lincd-xsd/ontologies/xsd';
import { ImageObject, } from 'lincd-schema/shapes/ImageObject';
import { Boolean } from 'lincd-xsd/shapes/Boolean';
import { ShapeSet } from 'lincd/collections/ShapeSet';
import { Event as SchemaEvent } from 'lincd-schema/shapes/Event';
import { Server } from 'lincd-server-utils/utils/Server';
let Event = class Event extends SchemaEvent {
    get attendance() {
        return this.getValue(irlcg.attendance);
    }
    set attendance(val) {
        this.overwrite(irlcg.attendance, new Literal(val));
    }
    get eventLogo() {
        return this.getOneAs(irlcg.eventLogo, ImageObject);
    }
    set eventLogo(val) {
        this.overwrite(irlcg.eventLogo, val.node);
    }
    get showPeacegameLogo() {
        return Boolean.isTrue(this.getOne(irlcg.showPeacegameLogo));
    }
    set showPeacegameLogo(val) {
        this.overwrite(irlcg.showPeacegameLogo, Boolean.toLiteral(val));
    }
    get attendanceInPerson() {
        return Boolean.isTrue(this.getOne(irlcg.attendanceInPerson));
    }
    set attendanceInPerson(val) {
        this.overwrite(irlcg.attendanceInPerson, Boolean.toLiteral(val));
    }
    get attendanceHome() {
        return Boolean.isTrue(this.getOne(irlcg.attendanceHome));
    }
    set attendanceHome(val) {
        this.overwrite(irlcg.attendanceHome, Boolean.toLiteral(val));
    }
    get isEventMode() {
        return Boolean.isTrue(this.getOne(irlcg.isEventMode));
    }
    set isEventMode(val) {
        this.overwrite(irlcg.isEventMode, Boolean.toLiteral(val));
    }
    get allowTeamSelection() {
        return Boolean.isTrue(this.getOne(irlcg.allowTeamSelection));
    }
    set allowTeamSelection(val) {
        this.overwrite(irlcg.allowTeamSelection, Boolean.toLiteral(val));
    }
    get pinned() {
        return Boolean.isTrue(this.getOne(irlcg.pinned));
    }
    set pinned(val) {
        this.overwrite(irlcg.pinned, Boolean.toLiteral(val));
    }
    get purpleTheme() {
        return Boolean.isTrue(this.getOne(irlcg.purpleTheme));
    }
    set purpleTheme(val) {
        this.overwrite(irlcg.purpleTheme, Boolean.toLiteral(val));
    }
    get route() {
        return this.getValue(irlcg.route);
    }
    set route(val) {
        this.overwrite(irlcg.route, val ? new Literal(val) : null);
    }
    get actions() {
        return this.getAllAs(irlcg.action, Action);
    }
    get defaultTeam() {
        // return this.getOneAs<Team>(irlcg.defaultTeam, Team);
        return this.getOneAs(irlcg.defaultTeam, getPackageShape('Team'));
    }
    set defaultTeam(val) {
        this.overwrite(irlcg.defaultTeam, val.node);
    }
    /**
     * Get Event by ID or identifier
     *
     * @param eventId ID or identifier of the event
     * @returns Event or undefined if not found
     */
    static getOne(eventId) {
        return Server.call(this, 'getOne', eventId);
    }
    static findGameboard(eventId) {
        return Server.call(this, 'findGameboard', eventId);
    }
    /**
     * Get all events (no date filtering).
     *
     * @returns Promise resolving to array of all events with id, identifier, name, description, and image
     */
    static getAll() {
        return Server.call(this, 'getAll');
    }
    /**
     * Get active events only.
     * Returns only events that have both startDate and endDate and are currently active
     * (start date <= today <= end date). Events without dates are excluded.
     *
     * @returns Promise resolving to array of active events with id, identifier, name, description, and image
     */
    static getActive() {
        return Server.call(this, 'getActive');
    }
};
Event.targetClass = irlcg.Event;
__decorate([
    literalProperty({
        path: irlcg.attendance,
        // required: true,
        datatype: xsd.string,
        maxCount: 1,
        description: 'Indicates if this event is in person (at a physical location) or from home (online).',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Event.prototype, "attendance", null);
__decorate([
    objectProperty({
        path: irlcg.eventLogo,
        shape: ImageObject,
        maxCount: 1,
    }),
    __metadata("design:type", ImageObject),
    __metadata("design:paramtypes", [ImageObject])
], Event.prototype, "eventLogo", null);
__decorate([
    literalProperty({
        path: irlcg.showPeacegameLogo,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "showPeacegameLogo", null);
__decorate([
    literalProperty({
        path: irlcg.attendanceInPerson,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "attendanceInPerson", null);
__decorate([
    literalProperty({
        path: irlcg.attendanceHome,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "attendanceHome", null);
__decorate([
    literalProperty({
        path: irlcg.isEventMode,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
        description: 'Event mode: true = simplified menu, event totals, event gameboard; false = full menu, regular totals.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "isEventMode", null);
__decorate([
    literalProperty({
        path: irlcg.allowTeamSelection,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
        description: 'Controls whether event registration can show a team selector. When enabled, signup may choose the default team or another team already associated with the event.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "allowTeamSelection", null);
__decorate([
    literalProperty({
        path: irlcg.pinned,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
        description: 'Pins this event to the top of quick-event lists.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "pinned", null);
__decorate([
    literalProperty({
        path: irlcg.purpleTheme,
        datatype: Boolean.targetClass,
        required: false,
        maxCount: 1,
        description: 'Uses the purple featured-event card theme.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], Event.prototype, "purpleTheme", null);
__decorate([
    literalProperty({
        path: irlcg.route,
        datatype: xsd.string,
        required: false,
        maxCount: 1,
        description: 'Optional application route opened by this event card instead of registration.',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Event.prototype, "route", null);
__decorate([
    objectProperty({
        path: irlcg.action,
        shape: Action,
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], Event.prototype, "actions", null);
__decorate([
    objectProperty({
        path: irlcg.defaultTeam,
        // shape: Team,
        shape: ['lincd-irlcg', 'Team'],
        maxCount: 1,
    }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Function])
], Event.prototype, "defaultTeam", null);
Event = __decorate([
    linkedShape({
        description: 'A unique kind of team created for specific events with start and end dates. Represents event-based teams with attendance tracking and time-bound participation. (event, event team)',
    })
], Event);
export { Event };
//# sourceMappingURL=Event.js.map